import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, FadeIn } from "@/components/Reveal";
import EclipseRing from "@/components/EclipseRing";
import Marquee from "@/components/Marquee";
import { IMAGES, PROJECTS } from "@/data/content";

const EXPERTISES = [
  { num: "01", label: "Identité visuelle", note: "Logo, charte graphique, papeterie" },
  { num: "02", label: "Supports print", note: "Brochures, flyers, catalogues" },
  { num: "03", label: "Réseaux sociaux", note: "Publications, templates, bannières" },
  { num: "04", label: "Site web", note: "Sites vitrines sur mesure" },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "55%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* ————— HERO ————— */}
      <section ref={heroRef} className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt=""
            className="h-[120%] w-full object-cover opacity-40 grayscale-[0.4]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-noir/70 via-noir/40 to-noir" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute -right-40 top-1/2 -translate-y-1/2 opacity-70 sm:-right-24"
        >
          <EclipseRing size={620} />
        </motion.div>

        <motion.div
          style={{ y: contentY, opacity: fade }}
          className="relative z-10 mx-auto max-w-5xl px-6 pt-24 text-center"
        >
          <FadeIn delay={0.2}>
            <p className="overline-label" data-testid="hero-overline">
              Studio de communication — Monteils, Gard
            </p>
          </FadeIn>

          <h1 className="mt-10 font-serif font-light leading-[0.98] tracking-tight text-ivoire">
            <MaskedLine delay={0.45} className="text-5xl sm:text-7xl lg:text-8xl">
              Créer
            </MaskedLine>
            <MaskedLine delay={0.6} className="text-5xl sm:text-7xl lg:text-8xl">
              ce qui <em className="italic text-champagne">demeure.</em>
            </MaskedLine>
          </h1>

          <FadeIn delay={1.1}>
            <p className="mx-auto mt-10 max-w-xl text-base leading-relaxed text-ivoire/70 sm:text-lg">
              Rémanence Studio conçoit des identités visuelles sobres, élégantes et
              intemporelles pour les entreprises et les particuliers qui veulent
              marquer les esprits.
            </p>
          </FadeIn>

          <FadeIn delay={1.3}>
            <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/contact"
                data-testid="hero-cta-devis"
                className="group flex items-center gap-3 rounded-full bg-champagne px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-noir transition-[background-color] duration-400 hover:bg-ivoire"
              >
                Demander un devis
                <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/le-studio"
                data-testid="hero-cta-studio"
                className="group flex items-center gap-3 rounded-full border border-ivoire/25 px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-ivoire transition-[border-color,color] duration-400 hover:border-champagne hover:text-champagne"
              >
                Découvrir le studio
              </Link>
            </div>
          </FadeIn>
        </motion.div>

        <FadeIn delay={1.8} className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2">
          <div className="flex flex-col items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.4em] text-pierre">Défiler</span>
            <motion.span
              className="block h-12 w-px bg-gradient-to-b from-champagne to-transparent"
              animate={{ scaleY: [1, 0.4, 1], opacity: [1, 0.4, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </FadeIn>
      </section>

      {/* ————— CONVICTION ————— */}
      <section className="relative mx-auto max-w-6xl px-6 py-28 sm:px-10 sm:py-40">
        <Reveal>
          <p className="overline-label">Notre conviction</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 max-w-4xl font-serif text-3xl font-light leading-tight tracking-tight text-ivoire sm:text-5xl lg:text-6xl">
            Une identité visuelle ne doit pas suivre les tendances.{" "}
            <em className="italic text-champagne">Elle doit traverser le temps.</em>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-10 md:grid-cols-12">
          <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
            <p className="text-base leading-relaxed text-ivoire/65">
              Nous créons des univers graphiques sobres, élégants et cohérents qui
              valorisent votre savoir-faire et inspirent confiance dès le premier
              regard. Parce qu'une image forte ne sert pas seulement à être
              remarquée : elle permet d'être reconnue, comprise et mémorisée.
            </p>
            <Link
              to="/le-studio"
              data-testid="conviction-link-studio"
              className="group mt-8 inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.2em] text-champagne"
            >
              Notre vision
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* ————— EXPERTISES ————— */}
      <section className="mx-auto max-w-6xl px-6 py-28 sm:px-10 sm:py-40">
        <div className="mb-16 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <Reveal>
            <p className="overline-label">Expertises</p>
            <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivoire sm:text-5xl">
              Ce que nous créons
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/services"
              data-testid="expertises-link-all"
              className="group inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.2em] text-ivoire/60 transition-colors duration-300 hover:text-champagne"
            >
              Tous nos services
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div>
          {EXPERTISES.map((exp, i) => (
            <Reveal key={exp.num} delay={i * 0.06}>
              <Link
                to="/services"
                data-testid={`expertise-row-${exp.num}`}
                className="group flex items-center justify-between gap-6 border-t border-white/8 py-8 transition-colors duration-500 last:border-b hover:border-champagne/40 sm:py-10"
              >
                <div className="flex items-baseline gap-6 sm:gap-12">
                  <span className="font-serif text-sm italic text-champagne/70">{exp.num}</span>
                  <div>
                    <h3 className="font-serif text-2xl font-light tracking-tight text-ivoire transition-colors duration-400 group-hover:text-champagne sm:text-4xl">
                      {exp.label}
                    </h3>
                    <p className="mt-2 text-sm text-pierre">{exp.note}</p>
                  </div>
                </div>
                <ArrowUpRight
                  size={26}
                  strokeWidth={1}
                  className="shrink-0 text-pierre transition-[color,transform] duration-400 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-champagne"
                />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ————— PROJETS ————— */}
      <section className="bg-charbon/40 py-28 sm:py-40">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal className="mb-20 text-center">
            <p className="overline-label">Travaux sélectionnés</p>
            <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivoire sm:text-5xl">
              Des univers qui <em className="italic text-champagne">marquent</em>
            </h2>
          </Reveal>

          <div className="grid gap-8 md:grid-cols-12">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <Reveal
                key={p.id}
                delay={i * 0.1}
                className={`md:col-span-4 ${i === 1 ? "md:mt-24" : ""} ${i === 2 ? "md:mt-48" : ""}`}
              >
                <Link to="/portfolio" data-testid={`home-project-${p.id}`} className="group block">
                  <div className={`overflow-hidden border border-white/8 ${p.ratio}`}>
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="img-luxe h-full w-full object-cover group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="mt-5 flex items-start justify-between">
                    <div>
                      <h3 className="font-serif text-xl font-light text-ivoire transition-colors duration-300 group-hover:text-champagne">
                        {p.name}
                      </h3>
                      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-pierre">{p.category}</p>
                    </div>
                    <ArrowUpRight size={18} strokeWidth={1} className="mt-1 text-pierre transition-colors duration-300 group-hover:text-champagne" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20 text-center">
            <Link
              to="/portfolio"
              data-testid="home-portfolio-link"
              className="group inline-flex items-center gap-3 rounded-full border border-ivoire/25 px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-ivoire transition-[border-color,color] duration-400 hover:border-champagne hover:text-champagne"
            >
              Voir le portfolio
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ————— CITATION ————— */}
      <section className="relative overflow-hidden py-32 sm:py-48">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.35 }}
          viewport={{ once: true }}
          transition={{ duration: 2 }}
          className="absolute -left-48 top-1/2 -translate-y-1/2"
        >
          <EclipseRing size={520} duration={90} />
        </motion.div>
        <div className="relative mx-auto max-w-4xl px-6 text-center sm:px-10">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-relaxed text-ivoire/85 sm:text-4xl sm:leading-relaxed">
              « Une belle identité attire le regard.{" "}
              <em className="italic text-champagne">Une identité cohérente inspire confiance.</em>{" "}
              Une identité durable devient un véritable outil de développement. »
            </p>
          </Reveal>
        </div>
      </section>

      {/* ————— CTA ————— */}
      <section className="border-t border-white/5 py-28 sm:py-40">
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-10">
          <Reveal>
            <p className="overline-label">Votre projet</p>
            <h2 className="mt-8 font-serif text-4xl font-light tracking-tight text-ivoire sm:text-6xl">
              Parlons de <em className="italic text-champagne">votre histoire.</em>
            </h2>
            <p className="mx-auto mt-8 max-w-lg text-base leading-relaxed text-ivoire/60">
              Chaque projet débute par une écoute attentive de votre activité, de vos
              objectifs et de votre personnalité.
            </p>
            <Link
              to="/contact"
              data-testid="home-final-cta"
              className="group mt-12 inline-flex items-center gap-3 rounded-full bg-champagne px-10 py-4 text-[13px] uppercase tracking-[0.2em] text-noir transition-[background-color] duration-400 hover:bg-ivoire"
            >
              Démarrer un projet
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
